import React from 'react';
import {Box} from "@mui/material";
import {StarFilledRounded} from "@mui-symbols-material/w300";
import {getInstalledBaseTypeIcon} from "@dreebit-registry/vsm-microservice-components";

// App yellow — equals `palette.warning.main` in src/provider/theming.tsx. Kept as a
// literal like the other hardcoded tree colours (#666666, #BD4200, …).
export const FAVORITE_STAR_COLOR = '#FFC860';

interface Props {
    nodeTypeKey?: string | null;
    size: number;
    color?: Parameters<typeof getInstalledBaseTypeIcon>[2];
    // Shows a small star at the bottom-right corner of the type icon: the node is
    // one of the user's favorites (`InstalledBaseNode.isStarred`).
    starred?: boolean;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Node type icon from the shared library with an optional favorite-star badge.
 * The library icons are raw `<svg>`s without className/sx support, so the badge
 * is layered by this wrapper instead of inside the icon.
 */
const InstalledBaseNodeTypeIcon = ({nodeTypeKey, size, color, starred}: Props) => {
    // The Material Symbols star only fills ~64% of its 960-unit viewBox, so the
    // font size has to be noticeably larger than the visible star: 18px on the
    // 22px tree row icon (~11.5px visible star), 11px on the 14px header icon.
    const badgeSize = clamp(Math.round(size * 0.8), 10, 22);
    // Pushes the star over the icon's bottom-right corner; the visible star ends
    // ~2px outside the icon box, its halo ~4px (the row's gap to the title).
    const offset = Math.round(badgeSize * 0.3);

    return (
        <Box
            data-starred={starred ? 'true' : undefined}
            sx={{
                position: 'relative',
                display: 'inline-flex',
                width: size,
                height: size,
                flexShrink: 0,
                lineHeight: 0,
            }}
        >
            {getInstalledBaseTypeIcon(nodeTypeKey ?? undefined, size, color)}
            {starred && (
                <StarFilledRounded
                    sx={{
                        position: 'absolute',
                        right: -offset,
                        bottom: -offset,
                        fontSize: badgeSize,
                        color: FAVORITE_STAR_COLOR,
                        overflow: 'visible',
                        // Halo cut-out between star and icon. With `paint-order` the fill
                        // covers the inner half of the stroke, so the visible halo is
                        // strokeWidth/2 = 120 viewBox units (~2px at 16px). Its colour
                        // follows the surrounding surface via `--favorite-halo` (the tree
                        // row sets it to its hover/selected grey) and defaults to white.
                        '& path': {
                            stroke: 'var(--favorite-halo, #fff)',
                            strokeWidth: 240,
                            strokeLinejoin: 'round',
                            paintOrder: 'stroke fill',
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default InstalledBaseNodeTypeIcon;
